import { initializeApp } from 'firebase/app';
import { secureLogger } from '@/utils/secure-logger';
import {
  getAuth,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  signInWithRedirect,
  GoogleAuthProvider,
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
// Firebase Production Configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyCoCEXZTxbtnJEzaF3deEm-NctYI-NwIgY',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'chatbotimoveisai.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'chatbotimoveisai',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'chatbotimoveisai.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '205197627950',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:205197627950:web:9a3e538b6000f2f50905bf',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-9HNF2J0EGJ',
};

// Initialize Firebase with fallback detection
let app: any;
let auth: any;
let db: any;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);

  // ✅ Configurar persistência LOCAL para login durar 30 dias
  // O Refresh Token do Firebase dura 1 ano e renova o ID Token automaticamente
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('✅ Firebase Auth persistence: LOCAL (30 dias de login)');
    })
    .catch(error => {
      console.warn('⚠️ Falha ao configurar persistência:', error);
    });

  // Initialize Firestore with proper error handling
  try {
    db = getFirestore(app);
    console.log('✅ Firestore initialized successfully');
  } catch (firestoreError) {
    console.warn(
      '⚠️ Firestore initialization failed, auth will work without database:',
      firestoreError
    );
    // Set db to null to allow auth to work without Firestore
    db = null;
  }

  console.log('✅ Firebase initialized successfully');
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  console.log('🔄 Will use fallback authentication system');
}

export { auth, db };

// User data interface
export interface UserData {
  uid: string;
  id?: string;
  email: string;
  name: string;
  displayName?: string;
  userTier: 'free' | 'starter' | 'professional' | 'enterprise';
  subscriptionStatus: 'active' | 'inactive' | 'trial' | 'cancelled';
  phone?: string;
  phoneNumber?: string; // Compatibilidade com Firebase User
  company?: string;
  role: 'owner' | 'admin' | 'agent' | 'backoffice' | 'investor';
  tenantId?: string;
  createdAt: Date;
  lastLogin?: Date;
  profileCompleted: boolean;
  isDemo?: boolean;
  organizadorPlan?: 'organizador_gratuito' | 'premium' | 'pro'; // Plan do Organizador de Clientes
}
// Auth service functions
export const authService = {
  // Create user account
  async createUser(email: string, password: string, userData: Partial<UserData>) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Update user profile
      await updateProfile(user, {
        displayName: userData.name,
      });
      // Create user document in Firestore (if available)
      const userDoc: UserData = {
        uid: user.uid,
        email: user.email,
        name: userData.name || '',
        userTier: userData.userTier || 'free',
        subscriptionStatus: userData.subscriptionStatus || 'trial',
        phone: userData.phone || '',
        company: userData.company || '',
        role: userData.role || 'agent',
        tenantId: userData.tenantId || null, // ✅ Evita undefined
        createdAt: new Date(),
        lastLogin: new Date(),
        profileCompleted: false,
      };

      if (db) {
        try {
          await setDoc(doc(db, 'users', user.uid), userDoc);
        } catch (error) {
          console.warn('⚠️ Failed to save user to Firestore, continuing with auth only:', error);
        }
      }

      return { user, userData: userDoc };
    } catch (error) {
      secureLogger.error('User creation failed', error, { component: 'firebase-auth' });
      throw error;
    }
  },
  // Sign in user
  async signIn(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create or update user document in Firestore (if available)
      if (db) {
        try {
          const userDocRef = doc(db, 'users', user.uid);

          // Create complete user document with all required fields
          await setDoc(
            userDocRef,
            {
              uid: user.uid,
              email: user.email || '',
              name: user.displayName || user.email?.split('@')[0] || 'User',
              userTier: 'free',
              subscriptionStatus: 'trial',
              role: 'agent',
              createdAt: new Date(),
              lastLogin: new Date(),
              updatedAt: new Date(),
              profileCompleted: false,
            },
            { merge: true }
          ); // merge: true preserves existing fields

          console.log('✅ User document created/updated successfully');
        } catch (error) {
          console.warn('⚠️ Failed to create/update user document:', error);
        }
      }

      // Get user data
      const userData = await this.getUserData(user.uid);
      return { user, userData };
    } catch (error) {
      secureLogger.security('Authentication failed', {
        error: error.code,
        component: 'firebase-auth',
      });
      throw error;
    }
  },
  // Sign out user
  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      secureLogger.error('Sign out failed', error, { component: 'firebase-auth' });
      throw error;
    }
  },
  // Get user data from Firestore with improved error handling
  async getUserData(uid: string): Promise<UserData | null> {
    // If Firestore is not available, create basic user data
    if (!db) {
      console.warn('⚠️ Firestore unavailable, creating basic user data');
      return {
        uid: uid,
        email: '',
        name: 'User',
        userTier: 'free',
        subscriptionStatus: 'trial',
        role: 'agent',
        createdAt: new Date(),
        profileCompleted: false,
      };
    }

    try {
      // Add timeout to prevent hanging on network issues (15s for mobile)
      const userDocPromise = getDoc(doc(db, 'users', uid));
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore timeout')), 15000)
      );

      const userDoc = (await Promise.race([userDocPromise, timeoutPromise])) as any;

      if (userDoc && userDoc.exists()) {
        const data = userDoc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLogin: data.lastLogin?.toDate(),
        } as UserData;
      }

      // User doesn't exist in Firestore, return basic data
      console.warn('⚠️ User not found in Firestore, creating basic data');
      return {
        uid: uid,
        email: '',
        name: 'User',
        userTier: 'free',
        subscriptionStatus: 'trial',
        role: 'agent',
        createdAt: new Date(),
        profileCompleted: false,
      };
    } catch (error) {
      console.warn('⚠️ Firestore error (network/timeout), creating basic user data:', error);
      // Return basic user data instead of throwing to prevent auth failures
      return {
        uid: uid,
        email: '',
        name: 'User',
        userTier: 'free',
        subscriptionStatus: 'trial',
        role: 'agent',
        createdAt: new Date(),
        profileCompleted: false,
      };
    }
  },
  // Update user data
  async updateUserData(uid: string, updates: Partial<UserData>) {
    if (!db) {
      console.warn('⚠️ Firestore unavailable, user data update skipped');
      return;
    }

    try {
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        ...updates,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.warn('⚠️ Failed to update user data in Firestore:', error);
      // Don't throw error, just log warning
    }
  },
  // Reset password
  async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      secureLogger.security('Password reset email failed', {
        component: 'firebase-auth',
        action: 'resetPassword',
        errorCode: error.code,
      });
      throw error;
    }
  },
  // Check if email exists
  async emailExists(email: string): Promise<boolean> {
    if (!db) {
      console.warn('⚠️ Firestore unavailable, skipping email check');
      return false;
    }

    try {
      const q = query(collection(db, 'users'), where('email', '==', email));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.warn('⚠️ Email existence check failed, assuming not exists:', error);
      return false;
    }
  },
  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      // Mark that we're initiating OAuth (will persist in URL after redirect)
      localStorage.setItem('oauth_flow_started', Date.now().toString());

      await signInWithRedirect(auth, provider);
      return { user: null, userData: null, redirecting: true };
    } catch (error) {
      localStorage.removeItem('oauth_flow_started');
      throw error;
    }
  },
  // Auth state listener
  onAuthStateChanged: (callback: (user: FirebaseUser | null) => void) => {
    return onAuthStateChanged(auth, callback);
  },
};
// Tenant service functions
export const tenantService = {
  // Create tenant
  async createTenant(tenantData: {
    name: string;
    slug: string;
    domain?: string;
    plan: string;
    ownerId: string;
  }) {
    try {
      const tenantDoc = {
        ...tenantData,
        createdAt: new Date(),
        status: 'active',
        settings: {},
        features: {},
      };
      const tenantRef = doc(collection(db, 'tenants'));
      await setDoc(tenantRef, tenantDoc);
      return { id: tenantRef.id, ...tenantDoc };
    } catch (error) {
      secureLogger.error('Tenant creation failed', error, {
        component: 'firebase-tenant',
        action: 'createTenant',
      });
      throw error;
    }
  },
  // Get tenant data
  async getTenantData(tenantId: string) {
    try {
      const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
      if (tenantDoc.exists()) {
        return { id: tenantDoc.id, ...tenantDoc.data() };
      }
      return null;
    } catch (error) {
      secureLogger.error('Tenant data fetch failed', error, {
        component: 'firebase-tenant',
        action: 'getTenantData',
      });
      throw error;
    }
  },
};
export default app;
